import { HttpClient, HttpParams } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {toCamel, toSnake} from 'convert-keys';
import {catchError, map} from "rxjs/operators";


@Injectable()
export class NavResourceService<T> {
  apiLocation: string;

  constructor(
    private httpClient: HttpClient,
  ) {
  }

  private static handleError(error: string): Observable<never> {
    return throwError(() => new Error(`${error}`));
  }

  get(path: string, params?: HttpParams, mappingCallback?: (response: unknown) => T): Observable<T> {
    return this.httpClient.get<T>(`${path}`, {params}).pipe(
      map(item => this.mapResponse(item, mappingCallback)),
      catchError(NavResourceService.handleError),
    );
  }

  getById(
    path: string,
    id: string | number,
    params?: HttpParams,
    mappingCallback?: (response: unknown) => T,
  ): Observable<T> {
    return this.httpClient.get<T>(`${path}/${id}`, {params}).pipe(
      map(item => this.mapResponse(item, mappingCallback)),
      catchError(NavResourceService.handleError),
    );
  }

  getList(path: string, params?: HttpParams, mappingCallback?: (response: unknown) => T): Observable<T[]> {
    return this.httpClient.get<T[]>(`${path}`, {params}).pipe(
      map(items => {
        const data = items['data'];
        if (!mappingCallback) {
          return data.map(p => toCamel<T>(p)) as T[];
        }
        return data.map(p => toCamel<T>(mappingCallback(p))) as T[];
      }),
      catchError(NavResourceService.handleError),
    );
  }

  getPaginated(path: string, index: number, page: number, params?: HttpParams): Observable<T[]> {
    const listParams = new HttpParams().set('limit', index.toString()).set('offset', page.toString());

    return this.httpClient
      .get<T[]>(`${path}?${listParams.toString()}`, {
        params,
      })
      .pipe(
        map(list => list['data'].map(p => toCamel<T>(p))),
        catchError(NavResourceService.handleError),
      );
  }

  add(
    path: string,
    resource: T | Partial<T>,
    params?: HttpParams,
    mappingCallback?: (response: unknown) => T,
  ): Observable<T> {
    return this.httpClient
      .post<T>(`${path}`, toSnake(resource), {
        params,
      })
      .pipe(
        map(item => this.mapResponse(item, mappingCallback)),
        catchError(NavResourceService.handleError),
      );
  }

  update(
    path: string,
    id: string | number,
    resource?: T | Partial<T> | T[] | Partial<T>[],
    params?: HttpParams,
    mappingCallback?: (response: unknown) => T,
  ): Observable<T> {
    return this.httpClient
      .put<T>(`${path}/${id}`, toSnake(resource), {
        params,
      })
      .pipe(
        map(item => this.mapResponse(item, mappingCallback)),
        catchError(NavResourceService.handleError),
      );
  }

  updatePath(
    path: string,
    resource?: T | Partial<T> | T[] | Partial<T>[],
    params?: HttpParams,
    mappingCallback?: (response: unknown) => T,
  ): Observable<T> {
    return this.httpClient
      .put<T>(`${path}`, toSnake(resource), {
        params,
      })
      .pipe(
        map(item => this.mapResponse(item, mappingCallback)),
        catchError(NavResourceService.handleError),
      );
  }

  delete(
    path: string,
    id: string | number,
    params?: HttpParams,
    mappingCallback?: (response: unknown) => T,
  ): Observable<T> {
    return this.httpClient.delete<T>(`${path}/${id}`, {params}).pipe(
      map(item => this.mapResponse(item, mappingCallback)),
      catchError(NavResourceService.handleError),
    );
  }

  private mapResponse(item, mappingCallback): T {
    if (!mappingCallback) {
      if (typeof item !== 'object') {
        return item;
      }
      try {
        return toCamel<T>(item);
      } catch (e) {
        console.log("Failed to camelize object", e);
        return item;
      }
    }
    try {
      return toCamel<T>(mappingCallback(item));
    } catch (e) {
      console.log("Failed to camelize object", e);
      return item;
    }
  }
}
